import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { errMsg, useTitle } from "../lib/api";

export default function AuthPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login, register, verify2fa, panelPath } = useAuth();
  const [mode, setMode] = useState(window.location.pathname === "/kayit" ? "register" : "login");
  const [accountType, setAccountType] = useState(params.get("tip") === "firma" ? "company" : "customer");
  const [twofa, setTwofa] = useState(null);
  const [forgotLink, setForgotLink] = useState(null);
  const [form, setForm] = useState({ name: "", surname: "", email: "", phone: "", password: "", company_name: "", code: "" });
  const [loading, setLoading] = useState(false);
  useTitle(mode === "login" ? "Giriş Yap" : "Kayıt Ol");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        const r = await api.post("/auth/forgot-password", { email: form.email });
        toast.success("Hesap varsa sıfırlama bağlantısı e-postanıza gönderildi");
        if (r.data.demo_token) setForgotLink(`/sifre-sifirla?token=${r.data.demo_token}`);
        return;
      }
      if (twofa) {
        const u = await verify2fa(twofa.email, form.code);
        toast.success("Hoş geldiniz!");
        navigate(panelPath(u));
        return;
      }
      const res = mode === "login"
        ? await login(form.email, form.password)
        : await register({ ...form, account_type: accountType });
      if (res?.requires_2fa) {
        setTwofa({ email: res.email, demo_code: res.demo_code });
        toast.success("Doğrulama kodu e-postanıza gönderildi");
      } else {
        toast.success(mode === "login" ? "Hoş geldiniz!" : "Hesabınız oluşturuldu!");
        navigate(panelPath(res));
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-14">
      <div className="card p-8">
        <div className="flex gap-2 mb-8 bg-[#070C1E]/70 rounded-xl p-1">
          <button data-testid="auth-tab-login" onClick={() => setMode("login")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-colors ${mode === "login" ? "bg-[#D4AF37] text-[#070C1E]" : "text-slate-400"}`}>
            Giriş Yap
          </button>
          <button data-testid="auth-tab-register" onClick={() => setMode("register")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-colors ${mode === "register" ? "bg-[#D4AF37] text-[#070C1E]" : "text-slate-400"}`}>
            Kayıt Ol
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {twofa && (
            <>
              <p className="text-sm text-slate-300 text-center">
                Yönetici girişi için <span className="text-[#D4AF37] font-semibold">{twofa.email}</span> adresine gönderilen 6 haneli kodu girin.
              </p>
              {twofa.demo_code && (
                <p className="text-xs text-slate-500 text-center">
                  Demo e-posta alanı — kod: <span className="font-mono text-[#D4AF37] font-bold" data-testid="twofa-demo-code">{twofa.demo_code}</span>
                </p>
              )}
              <div>
                <label className="label">Doğrulama Kodu</label>
                <input data-testid="twofa-code" className="input text-center text-2xl tracking-[0.4em] font-mono" maxLength={6} required value={form.code} onChange={set("code")} placeholder="••••••" autoComplete="one-time-code" />
              </div>
              <button data-testid="twofa-submit-btn" disabled={loading} className="btn-gold w-full">
                {loading ? "Doğrulanıyor..." : "Doğrula ve Giriş Yap"}
              </button>
              <button type="button" data-testid="twofa-back-btn" onClick={() => setTwofa(null)} className="w-full text-center text-xs text-slate-500 hover:text-slate-300">← Geri dön</button>
            </>
          )}
          {!twofa && mode === "register" && (
            <>
              <div className="flex gap-2">
                <button type="button" data-testid="register-type-customer" onClick={() => setAccountType("customer")}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${accountType === "customer" ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-slate-400"}`}>
                  Müşteri
                </button>
                <button type="button" data-testid="register-type-company" onClick={() => setAccountType("company")}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${accountType === "company" ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-slate-400"}`}>
                  Firma
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Ad</label>
                  <input data-testid="register-name" className="input" required value={form.name} onChange={set("name")} />
                </div>
                <div>
                  <label className="label">Soyad</label>
                  <input data-testid="register-surname" className="input" value={form.surname} onChange={set("surname")} />
                </div>
              </div>
              {accountType === "company" && (
                <div>
                  <label className="label">Firma Adı</label>
                  <input data-testid="register-company-name" className="input" required value={form.company_name} onChange={set("company_name")} placeholder="Örn: Yıldız Organizasyon" />
                </div>
              )}
              <div>
                <label className="label">Telefon</label>
                <input data-testid="register-phone" className="input" value={form.phone} onChange={set("phone")} />
              </div>
            </>
          )}
          {!twofa && mode !== "forgot" && (
            <>
              <div>
                <label className="label">E-posta</label>
                <input data-testid="auth-email" type="email" className="input" required value={form.email} onChange={set("email")} />
              </div>
              <div>
                <label className="label">Şifre</label>
                <input data-testid="auth-password" type="password" className="input" required value={form.password} onChange={set("password")} />
              </div>
              <button data-testid="auth-submit-btn" disabled={loading} className="btn-gold w-full">
                {loading ? "İşleniyor..." : mode === "login" ? "Giriş Yap" : accountType === "company" ? "Ücretsiz Firma Kaydı" : "Kayıt Ol"}
              </button>
              {mode === "login" && (
                <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")} className="w-full text-center text-xs text-slate-500 hover:text-[#D4AF37] transition-colors">
                  Şifremi Unuttum
                </button>
              )}
            </>
          )}
          {!twofa && mode === "forgot" && (
            <>
              <p className="text-sm text-slate-300 text-center">Kayıtlı e-posta adresinizi girin, size şifre sıfırlama bağlantısı gönderelim.</p>
              <div>
                <label className="label">E-posta</label>
                <input data-testid="forgot-email" type="email" className="input" required value={form.email} onChange={set("email")} />
              </div>
              <button data-testid="forgot-submit-btn" disabled={loading} className="btn-gold w-full">
                {loading ? "Gönderiliyor..." : "Sıfırlama Bağlantısı Gönder"}
              </button>
              {forgotLink && (
                <p className="text-xs text-slate-500 text-center">
                  Demo e-posta alanı — <Link className="text-[#D4AF37] underline" to={forgotLink} data-testid="forgot-demo-link">sıfırlama bağlantısına git</Link>
                </p>
              )}
              <button type="button" onClick={() => { setMode("login"); setForgotLink(null); }} className="w-full text-center text-xs text-slate-500 hover:text-slate-300">← Girişe dön</button>
            </>
          )}
        </form>

      </div>
      <p className="text-center text-xs text-slate-600 mt-6">
        <Link to="/" className="hover:text-[#D4AF37]">← Ana sayfaya dön</Link>
      </p>
    </div>
  );
}
